import { useEffect, useRef, useState } from "react";
import type { Feedback, RenderExercise } from "../hooks.js";
import {
  IconCheck,
  IconEaser,
  IconLevel,
  IconPause,
  IconPlay,
  IconTighter,
} from "../components/Icons.js";
import FeedbackForm from "../components/FeedbackForm.js";

export type { Feedback };
export type Phase = "exercise" | "feedback" | "done";

/**
 * The break itself, rendered in the column.
 *
 * Direction is D-01: the clinician's reasoning is available but never required,
 * and the player is terse and text-primary with video as the optional layer. The
 * steps and the clinical note are always on screen, which is also what makes the
 * text-only path a first-class mode rather than an error state.
 */

/**
 * Build the Vimeo embed URL.
 *
 * The stored value is a bare `player.vimeo.com/video/{id}`, which loads a paused
 * player. That left the timer counting down over a still frame, which is the
 * kind of detail that reads as broken even when nobody can say why.
 *
 * Muted is not optional: browsers refuse to autoplay an unmuted video, and these
 * clips have no narration to lose. Looping matters because the clinician's
 * prescribed duration and the clip length are two different numbers, so a short
 * clip should keep demonstrating for as long as the exercise lasts.
 *
 * Section 4.4a: motion respects prefers-reduced-motion, including this autoplay,
 * with a still-plus-steps path instead. The steps are always on screen anyway,
 * so the fallback is already there.
 */
function embedUrl(base: string | null): string | undefined {
  if (!base) return undefined;
  // Never autoplay on load: the clip is started by message once the
  // get-ready countdown has run, and under reduced motion not at all.
  // Hold is the one control: the player's own bar is asked to stay hidden
  // (controls, honoured on paid Vimeo plans) and the frame ignores clicks
  // and keys regardless, so pausing the clip cannot drift from the clock.
  const p = new URLSearchParams({
    title: "0",
    byline: "0",
    portrait: "0",
    badge: "0",
    controls: "0",
    keyboard: "0",
    muted: "1",
    loop: "1",
    autoplay: "0",
  });
  return `${base}?${p.toString()}`;
}

/**
 * A moment before the clip. Five seconds on the dark stage with the count,
 * so the person can settle, read the first step and be ready when the
 * movement starts, rather than arriving mid-clip with the clock already
 * running. The exercise clock and the video both wait for it. It is
 * skippable, and it still shows under reduced motion because it is a timer,
 * not an animation.
 */
export const READY_SECONDS = 5;
const RING_R = 45;
const RING_C = 2 * Math.PI * RING_R;

/** Tracks the OS reduced-motion setting, and keeps tracking it if it changes. */
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return reduce;
}

export function StepPips({ index, total }: { index: number; total: number }) {
  return (
    <div className="pips" aria-label={`Step ${index + 1} of ${total}`}>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`pip${i === index ? " pip-active" : ""}${
            i < index ? " pip-done" : ""
          }`}
        />
      ))}
    </div>
  );
}

export function BreakStep({
  exercise,
  index,
  total,
  showIntro,
  intro,
  onNext,
  onStop,
}: {
  exercise: RenderExercise;
  index: number;
  total: number;
  showIntro: boolean;
  intro: string;
  onNext: () => void;
  onStop: () => void;
}) {
  const [secondsLeft, setSecondsLeft] = useState(exercise.durationSeconds);
  const [held, setHeld] = useState(false);
  const [ready, setReady] = useState(READY_SECONDS);
  const started = ready <= 0;
  const reduceMotion = usePrefersReducedMotion();
  const frame = useRef<HTMLIFrameElement>(null);
  const clip = useRef<HTMLVideoElement>(null);
  // Our own copy of the clip when the API sends one (D-52); Vimeo if it
  // sends none or our copy fails to load.
  const [ownFailed, setOwnFailed] = useState(false);
  const own = exercise.videoUrl !== null && !ownFailed;

  // Reset the clock whenever the exercise changes.
  useEffect(() => {
    setSecondsLeft(exercise.durationSeconds);
    setHeld(false);
    setReady(READY_SECONDS);
    setOwnFailed(false);
  }, [exercise]);

  // The get-ready count. Reaching zero is what starts everything else.
  useEffect(() => {
    if (ready <= 0) return;
    const t = setTimeout(() => setReady((r) => r - 1), 1000);
    return () => clearTimeout(t);
  }, [ready]);

  /**
   * Keep the clip in step with the clock.
   *
   * The timer and the Vimeo player are two independent clocks, so holding one
   * left the other running: the countdown stopped while the person carried on
   * being shown the movement. Vimeo's embedded player accepts commands over
   * postMessage, which means this needs no SDK and no new dependency.
   */
  useEffect(() => {
    if (own) {
      const v = clip.current;
      if (!v || !started) return;
      if (held || reduceMotion) v.pause();
      else void v.play().catch(() => undefined);
      return;
    }
    const win = frame.current?.contentWindow;
    if (!win) return;
    // Before the countdown ends there is nothing to send: the clip loaded
    // paused. Pausing is always safe to send after that. Playing is not:
    // under reduced motion the clip is deliberately not running, and
    // resuming the *timer* should not quietly start a video the person asked
    // not to have moving (section 4.4a).
    if (!started) return;
    const playing = !held;
    if (playing && reduceMotion) return;
    const send = () =>
      win.postMessage(
        JSON.stringify({ method: playing ? "play" : "pause" }),
        "https://player.vimeo.com"
      );
    send();
    // The player ignores commands sent before it has finished loading, so try
    // again shortly after. Cheap, and it covers a slow first frame.
    const retries = [setTimeout(send, 500), setTimeout(send, 1500)];
    return () => retries.forEach(clearTimeout);
  }, [started, held, reduceMotion, own]);

  // The frame does not take clicks, so under reduced motion this is the only
  // way to start the clip, and it is the person's own choice.
  function playClip() {
    if (own) {
      void clip.current?.play().catch(() => undefined);
      return;
    }
    frame.current?.contentWindow?.postMessage(JSON.stringify({ method: "play" }), "https://player.vimeo.com");
  }

  function stopEverything() {
    clip.current?.pause();
    frame.current?.contentWindow?.postMessage(
      JSON.stringify({ method: "pause" }),
      "https://player.vimeo.com"
    );
    onStop();
  }

  /**
   * The clock runs the exercise, and reaching zero advances on its own.
   *
   * To be accurate about where this comes from: section 7.4 item 14 specifies a
   * per-exercise timer, and a timer that expires and then does nothing is not a
   * timer. The auto-advance is that inference, not a quoted rule. Section 4.3's
   * "remove the choice" is about *starting* a break without configuring it, and
   * E5's "one-tap auto-playing breaks" says the same thing. Neither says a
   * person may not skip, and section 4.5 plus the copy bank's "You started;
   * stopping early is fine" say plainly that they may.
   *
   * Holding is what keeps the auto-advance inside the accessibility floor.
   * Section 4.4a names WCAG SC 2.2.1 Timing Adjustable, and an advance nobody
   * can stop would fail it. The hold control is always present rather than
   * appearing at the end, per D-14.
   */
  useEffect(() => {
    if (!started || held) return;
    if (secondsLeft <= 0) {
      const done = setTimeout(onNext, 600);
      return () => clearTimeout(done);
    }
    const tick = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(tick);
  }, [started, secondsLeft, held, onNext]);

  const progress = 1 - secondsLeft / exercise.durationSeconds;
  const isLast = index === total - 1;
  const finished = secondsLeft <= 0;

  return (
    <div className="break">
      <StepPips index={index} total={total} />

      {/* Pathway intro on the first step only, per section 7.4 item 14. */}
      {showIntro && <p className="break-intro">{intro}</p>}

      <div className="break-meta">
        <span className="break-step">
          Step {index + 1} of {total}
        </span>
      </div>

      <h2 className="break-name">{exercise.name}</h2>

      {/*
        The clock sits on the video rather than under it.
        
        D-01 settled the player as text-primary with video optional, and the
        previous layout inverted that: a large video, then a clock block, then a
        progress bar, which pushed the written steps below the fold in a 400px
        column. Signposting that scroll would have been treating the symptom.
        Overlaying the clock recovers about 90px and puts the steps back on
        screen, which is where the brief wanted them in the first place.
      */}
      <div className="break-stage">
        <div className="break-video">
          {own ? (
            <video
              key={exercise.id}
              ref={clip}
              src={exercise.videoUrl ?? undefined}
              poster={exercise.posterUrl ?? undefined}
              muted
              loop
              playsInline
              preload="auto"
              disablePictureInPicture
              tabIndex={-1}
              aria-label={exercise.name}
              onError={() => setOwnFailed(true)}
            />
          ) : (
            <iframe
              key={exercise.id}
              ref={frame}
              src={embedUrl(exercise.vimeoEmbed)}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={exercise.name}
            />
          )}
        </div>

        {!started && (
          <div className="break-ready">
            <p className="break-ready-kicker" role="status">
              Get ready
            </p>
            <div className="break-ready-ring">
              <svg viewBox="0 0 96 96" aria-hidden>
                <circle className="break-ready-track" cx="48" cy="48" r={RING_R} />
                <circle
                  className="break-ready-arc"
                  cx="48"
                  cy="48"
                  r={RING_R}
                  strokeDasharray={RING_C}
                  strokeDashoffset={RING_C * (1 - ready / READY_SECONDS)}
                />
              </svg>
              <span className="break-ready-num" role="timer" aria-live="off">
                {ready}
              </span>
            </div>
            {exercise.whyText ? (
              <p className="break-ready-line">
                <span className="break-ready-why">Why it helps</span>
                {exercise.whyText}
              </p>
            ) : (
              <p className="break-ready-line">Settle in. The clip starts in a moment.</p>
            )}
            <button type="button" className="break-ready-skip" onClick={() => setReady(0)}>
              Start now
            </button>
          </div>
        )}

        {started && (
          <div className={`break-clock${held ? " held" : ""}`}>
            <span className="break-count" role="timer" aria-live="off">
              {Math.max(secondsLeft, 0)}
              <span className="break-count-unit">s</span>
            </span>
            <button
              type="button"
              className={`break-hold${held ? " held" : ""}`}
              onClick={() => setHeld((h) => !h)}
            >
              {held ? <IconPlay size={14} /> : <IconPause size={14} />}
              {held ? "Resume" : "Hold"}
            </button>
          </div>
        )}

        <div className="break-progress">
          <div
            className={`break-progress-fill${held ? " paused" : ""}`}
            style={{ width: `${Math.min(progress, 1) * 100}%` }}
          />
        </div>
      </div>

      {started && reduceMotion && (
        <p className="break-stillnote">
          Video is not playing automatically because your system asks for reduced
          motion. Follow the steps, or{" "}
          <button type="button" className="break-stillplay" onClick={() => playClip()}>
            play the clip
          </button>
          .
        </p>
      )}

      {held && (
        <p className="break-held" role="status">
          Held. Take as long as you need.
        </p>
      )}

      {/* Text is a first-class mode, not a fallback. D-01, and the POC README. */}
      <p className="break-steps-label">What to do</p>
      <ol className="break-steps">
        {exercise.steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>

      {/*
        The clinician's note and the way out sit together on purpose. Most of
        these notes say some version of "stop if this increases your pain", and
        an instruction to stop is not much use next to an interface that only
        offers Next. Someone who needs this is in discomfort and is not going to
        hunt for the panel's close button.

        Labelled "Stop" rather than anything more spoken. The section 10 copy
        bank has no approved line for stopping mid-break, and the POC's
        convention is that nothing user-facing ships without checking section 10.
        Plain interface vocabulary sidesteps that: "Stop" is the same category of
        word as "Close" or "Next", so there is no new voice to sign off.
      */}
      {exercise.clinicalNote && (
        <p className="break-note">
          <span className="break-note-label">Take care</span>
          {exercise.clinicalNote}
        </p>
      )}

      {/* The clinician's gentler version, folded away so the steps stay the steps. */}
      {exercise.fallbackText && (
        <details className="break-easier">
          <summary>Easier option</summary>
          <p>{exercise.fallbackText}</p>
        </details>
      )}

      {/*
        Pinned to the bottom of the column rather than sitting at the end of the
        content. In a 400px panel the buttons fell below the fold on most laptop
        heights, with nothing to say they were there.

        That matters most for the way out: the whole point of putting a stop
        control next to the clinical note is that someone whose back has just
        objected can reach it immediately, and "scroll down to find it" is not
        immediately. The steps and the note scroll; the decisions do not.
      */}
      <div className="break-actions">
        <button className="break-next" onClick={onNext}>
          {finished
            ? isLast
              ? "Finishing…"
              : "Next up…"
            : isLast
              ? "Finish break"
              : "Next exercise"}
        </button>
        <button type="button" className="break-stop" onClick={stopEverything}>
          Stop
        </button>
        {/* Copy bank, section 10. Reward on start, said where it matters. */}
        <p className="break-out">You started, stopping early is fine.</p>
      </div>
    </div>
  );
}

/**
 * Completion and feedback, on one surface. Section 7.4 items 17 and 18.
 *
 * The brief numbers them as two screens and lists completion **first**: "the
 * reward moment. Reward on start, not perfection." An earlier version here asked
 * the question first, which put a request before the acknowledgement and left
 * the question floating alone in a 400px column with nothing attached to it.
 *
 * They are merged rather than sequenced because two near-empty screens back to
 * back in a narrow panel is worse than one that reads in a single glance. The
 * order the brief cares about is preserved: you are told it counted, and then
 * asked something optional.
 *
 * The question is pinned to post-break wording per D-09: "How's your back?"
 * invites *better than what*, and this answer may reach pilot analysis, so the
 * comparison has to be fixed.
 *
 * The three options carry equal visual weight, with no colour separating them
 * until you touch one. Tinting "Worse" as a warning would quietly discourage
 * people from choosing it, and a feedback control that leans on the answer
 * produces data not worth collecting.
 */
export function FeedbackStep({ onChoose }: { onChoose: (c: Feedback) => void }) {
  const OPTIONS: { value: Feedback; label: string; icon: typeof IconEaser }[] = [
    { value: "better", label: "Better", icon: IconEaser },
    { value: "same", label: "Same", icon: IconLevel },
    { value: "worse", label: "Worse", icon: IconTighter },
  ];

  return (
    <div className="feedback">
      <div className="done-tick" aria-hidden>
        <IconCheck size={26} />
      </div>

      {/* Copy bank, section 10. The reward, before anything is asked. */}
      <h2 className="done-head">That counts.</h2>
      <p className="done-sub">You started; stopping early is fine.</p>

      <div className="feedback-ask">
        <p className="feedback-eyebrow">One more thing, if you like</p>
        <h3 className="feedback-head">How does your back feel now?</h3>

        <div className="feedback-choices">
          {OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`fb-opt fb-${o.value}`}
              onClick={() => onChoose(o.value)}
            >
              <span className="fb-icon" aria-hidden>
                <o.icon size={22} />
              </span>
              <span className="fb-label">{o.label}</span>
            </button>
          ))}
        </div>

        {/*
          Section 6.4: skipping is fine and should be stated, along with the
          reason it costs nothing. Saying the break already counted is what makes
          the skip genuinely free rather than nominally available.
        */}
        <button
          type="button"
          className="feedback-skip"
          onClick={() => onChoose("same")}
        >
          Skip
        </button>
        <p className="feedback-free">Skipping is fine. The break already counted.</p>
      </div>
    </div>
  );
}

/**
 * After the answer. Deliberately brief.
 *
 * The reward has already been given on the previous surface, so this only
 * confirms what was recorded and gets out of the way. Section 2: the app is
 * where the break happens, not where the relationship lives.
 */
export function Completion({
  feedback,
  stopped = false,
  onClose,
}: {
  feedback: Feedback | null;
  stopped?: boolean;
  onClose: () => void;
}) {
  const [noteOpen, setNoteOpen] = useState(false);
  return (
    <div className="done done-after">
      <div className="done-tick done-tick-sm" aria-hidden>
        <IconCheck size={20} />
      </div>
      <h2 className="done-head done-head-sm">
        {stopped ? "That counts." : "Logged."}
      </h2>
      <p className="done-sub">
        {stopped
          ? "You started, and that is what gets recorded."
          : "Back to what you were doing."}
      </p>
      {feedback && !stopped && (
        <p className="done-fb">
          You said your back feels <b>{feedback}</b>.
        </p>
      )}
      <button className="done-btn" onClick={onClose}>
        Close
      </button>
      {noteOpen ? (
        <div className="done-note">
          <FeedbackForm screen={stopped ? "break-stopped" : "break-complete"} />
        </div>
      ) : (
        <button type="button" className="done-note-link" onClick={() => setNoteOpen(true)}>
          Something to tell us about that break?
        </button>
      )}
    </div>
  );
}
